import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Map, CheckCircle, Clock, ChevronRight, ArrowRight,
  Zap, Users, Calendar, BarChart2, AlertTriangle,
  Database, GitBranch, Shield, Layers, Target,
} from 'lucide-react';
import {
  getImplementationRegistry,
  getOverallProgress,
  getPhaseStatusColor,
  getPhaseStatusLabel,
} from '../../lib/implementationRegistry';

const TABS = ['Overview', 'Systems', 'Workstreams'] as const;
type Tab = typeof TABS[number];

const SYSTEM_ICONS: Record<string, React.ReactNode> = {
  dynamics_365: <Layers className="w-5 h-5" />,
  cdp: <Database className="w-5 h-5" />,
  lms: <GitBranch className="w-5 h-5" />,
  siebel: <Shield className="w-5 h-5" />,
};

const SYSTEM_COLORS: Record<string, string> = {
  dynamics_365: 'bg-blue-600',
  cdp: 'bg-purple-600',
  lms: 'bg-emerald-600',
  siebel: 'bg-amber-600',
};

export default function ImplementationOverview() {
  const { i18n } = useTranslation();
  const isAr = i18n.language === 'ar';
  const [activeTab, setActiveTab] = useState<Tab>('Overview');

  const registry = getImplementationRegistry();
  const { info, phases, systems, workstreams, criticalShifts } = registry;
  const overallProgress = getOverallProgress();

  const completedPhases = phases.filter((p) => p.meta.status === 'completed').length;
  const inProgressPhases = phases.filter((p) => p.meta.status === 'in_progress').length;

  const summaryStats = [
    { label: 'Phases', labelAr: 'المراحل', value: phases.length, icon: Map, color: 'text-indigo-600' },
    { label: 'Completed', labelAr: 'مكتمل', value: completedPhases, icon: CheckCircle, color: 'text-green-600' },
    { label: 'In Progress', labelAr: 'جارٍ', value: inProgressPhases, icon: Zap, color: 'text-blue-600' },
    { label: 'Pilot Target', labelAr: 'هدف التجربة', value: 'Jun 30', icon: Target, color: 'text-rose-600' },
    { label: 'Branches', labelAr: 'الفروع', value: info.totalOwnedBranches, icon: Users, color: 'text-teal-600' },
    { label: 'Sub-Dealers', labelAr: 'الموزعون', value: info.totalSubDealers, icon: Calendar, color: 'text-orange-600' },
  ];

  return (
    <div className={`max-w-7xl mx-auto px-4 py-8 ${isAr ? 'rtl' : 'ltr'}`} dir={isAr ? 'rtl' : 'ltr'}>

      {/* Header */}
      <div className="mb-8">
        <div className="flex items-start gap-4">
          <div className="p-3 bg-indigo-600 rounded-xl flex-shrink-0">
            <Map className="w-7 h-7 text-white" />
          </div>
          <div>
            <div className="flex items-center gap-2 mb-1">
              <span className="text-xs font-semibold text-indigo-600 uppercase tracking-wide bg-indigo-50 px-2 py-0.5 rounded-full">
                Business Transformation Programme
              </span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900">
              {isAr ? 'دليل التنفيذ' : 'Implementation Guide'}
            </h1>
            <p className="text-gray-500 text-sm mt-1 max-w-2xl">
              {isAr
                ? info.descriptionAr
                : 'Four platforms. Six workstreams. Seven phases. Pilot must complete by June 30, 2026.'}
            </p>
          </div>
        </div>
      </div>

      {/* Summary stats */}
      <div className="grid grid-cols-3 sm:grid-cols-6 gap-3 mb-8">
        {summaryStats.map((s) => {
          const Icon = s.icon;
          return (
            <div key={s.label} className="bg-white rounded-xl border border-gray-200 p-4 text-center shadow-sm">
              <Icon className={`w-5 h-5 ${s.color} mx-auto mb-1.5`} />
              <div className="text-xl font-bold text-gray-900">{s.value}</div>
              <div className="text-xs text-gray-500 mt-0.5">{isAr ? s.labelAr : s.label}</div>
            </div>
          );
        })}
      </div>

      {/* Overall progress */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6 mb-6">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-base font-bold text-gray-900">
              {isAr ? 'التقدم الكلي في التنفيذ' : 'Overall Programme Progress'}
            </h2>
            <p className="text-xs text-gray-400 mt-0.5">
              {isAr ? `بدأ ${info.programmeStart} · هدف التجربة ${info.pilotDeadline}` : `Started ${info.programmeStart} · Pilot target ${info.pilotDeadline}`}
            </p>
          </div>
          <span className="text-2xl font-bold text-indigo-600">{overallProgress}%</span>
        </div>
        <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
          <div
            className="h-3 rounded-full bg-gradient-to-r from-indigo-500 to-indigo-600 transition-all"
            style={{ width: `${overallProgress}%` }}
          />
        </div>
        <div className="flex items-center justify-between mt-3">
          <div className="flex gap-4 text-xs text-gray-500">
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-green-500 inline-block" />{completedPhases} {isAr ? 'مكتمل' : 'completed'}</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-blue-500 inline-block" />{inProgressPhases} {isAr ? 'جارٍ' : 'in progress'}</span>
            <span className="flex items-center gap-1.5"><span className="w-2 h-2 rounded-full bg-gray-300 inline-block" />{phases.length - completedPhases - inProgressPhases} {isAr ? 'لم يبدأ' : 'not started'}</span>
          </div>
          <Link to="/implementation/tracker" className="text-xs text-indigo-600 hover:underline flex items-center gap-1">
            {isAr ? 'عرض المتتبع الكامل' : 'Full Tracker'} <ArrowRight className="w-3 h-3" />
          </Link>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 bg-gray-100 rounded-xl p-1 mb-6 w-fit">
        {TABS.map((tab) => (
          <button
            key={tab}
            onClick={() => setActiveTab(tab)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-all ${
              activeTab === tab
                ? 'bg-white text-gray-900 shadow-sm'
                : 'text-gray-500 hover:text-gray-700'
            }`}
          >
            {tab}
          </button>
        ))}
      </div>

      {/* ── Tab: Overview ── */}
      {activeTab === 'Overview' && (
        <div className="space-y-4">
          {/* Phase cards */}
          <h2 className="text-lg font-bold text-gray-900 mb-2">
            {isAr ? 'مراحل البرنامج' : 'Programme Phases'}
          </h2>
          {phases.map((phase, idx) => {
            const statusColor = getPhaseStatusColor(phase.meta.status);
            const statusLabel = getPhaseStatusLabel(phase.meta.status, isAr);
            const isLast = idx === phases.length - 1;

            return (
              <div key={phase.meta.id} className="relative">
                {!isLast && (
                  <div className="absolute left-6 top-full w-0.5 h-4 bg-gray-200 z-10 hidden md:block" />
                )}
                <Link
                  to={`/implementation/phase/${phase.meta.id}`}
                  className="block bg-white rounded-2xl border-2 border-gray-200 hover:border-indigo-300 hover:shadow-md transition-all group p-5"
                >
                  <div className="flex items-start gap-4">
                    {/* Phase number bubble */}
                    <div
                      className="w-11 h-11 rounded-xl flex items-center justify-center text-white font-bold text-base flex-shrink-0"
                      style={{ backgroundColor: phase.meta.color }}
                    >
                      {phase.meta.number}
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-3 mb-1.5">
                        <div>
                          <div className="flex items-center gap-2 flex-wrap mb-0.5">
                            <span className="text-xs text-gray-400 font-semibold uppercase tracking-wide">
                              {isAr ? `المرحلة ${phase.meta.number}` : `Phase ${phase.meta.number}`}
                            </span>
                            <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${statusColor}`}>
                              {statusLabel}
                            </span>
                            {phase.meta.durationWeeks > 0 && (
                              <span className="text-xs text-gray-400 flex items-center gap-1">
                                <Clock className="w-3 h-3" />
                                {phase.meta.durationWeeks}w
                              </span>
                            )}
                            {phase.meta.startDate && (
                              <span className="text-xs text-gray-400">{phase.meta.startDate}</span>
                            )}
                          </div>
                          <h3 className="text-base font-bold text-gray-900 group-hover:text-indigo-700 transition-colors">
                            {isAr ? phase.meta.titleAr : phase.meta.titleEn}
                          </h3>
                          <p className="text-sm text-gray-500 mt-0.5 line-clamp-1">
                            {isAr ? phase.meta.subtitleAr : phase.meta.subtitleEn}
                          </p>
                        </div>
                        <ChevronRight className={`w-5 h-5 text-gray-300 flex-shrink-0 mt-1 group-hover:text-indigo-500 transition-transform group-hover:translate-x-1 ${isAr ? 'rotate-180' : ''}`} />
                      </div>

                      {/* Progress bar */}
                      {phase.meta.progress > 0 && (
                        <div className="mb-2">
                          <div className="w-full bg-gray-100 rounded-full h-1.5">
                            <div
                              className="h-1.5 rounded-full"
                              style={{ width: `${phase.meta.progress}%`, backgroundColor: phase.meta.color }}
                            />
                          </div>
                          <span className="text-xs text-gray-400 mt-0.5 inline-block">{phase.meta.progress}%</span>
                        </div>
                      )}

                      {/* Active workstreams */}
                      <div className="flex flex-wrap gap-1.5">
                        {phase.meta.activeWorkstreams.slice(0, 4).map((wsId) => {
                          const ws = workstreams.find((w) => w.id === wsId);
                          return ws ? (
                            <span
                              key={wsId}
                              className="text-xs px-2 py-0.5 rounded-full font-medium text-white"
                              style={{ backgroundColor: ws.color }}
                            >
                              {ws.label}
                            </span>
                          ) : null;
                        })}
                        {phase.meta.activeWorkstreams.length > 4 && (
                          <span className="text-xs text-indigo-500 font-medium">
                            +{phase.meta.activeWorkstreams.length - 4}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </Link>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Tab: Systems ── */}
      {activeTab === 'Systems' && (
        <div className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {systems.map((sys) => (
              <div
                key={sys.id}
                className="bg-white rounded-2xl border border-gray-200 shadow-sm p-6"
              >
                <div className="flex items-start gap-3 mb-4">
                  <div className={`p-2.5 rounded-xl text-white flex-shrink-0 ${SYSTEM_COLORS[sys.id] ?? 'bg-gray-600'}`}>
                    {SYSTEM_ICONS[sys.id] ?? <Layers className="w-5 h-5" />}
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 text-sm leading-tight">{sys.name}</h3>
                    <p className="text-xs text-gray-500 mt-0.5">{sys.role}</p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-gray-400 w-16 flex-shrink-0 pt-0.5">Owner</span>
                    <span className="text-xs text-gray-700 font-medium">{sys.owner}</span>
                  </div>
                  <div className="flex items-start gap-2">
                    <span className="text-xs font-semibold text-gray-400 w-16 flex-shrink-0 pt-0.5">State</span>
                    <span className="text-xs text-gray-700">{sys.currentState}</span>
                  </div>
                  {sys.pipelineStagesOwned.length > 0 && (
                    <div>
                      <span className="text-xs font-semibold text-gray-400 block mb-1.5">Stages Owned</span>
                      <div className="flex flex-wrap gap-1">
                        {sys.pipelineStagesOwned.map((s) => (
                          <span key={s} className="text-xs bg-gray-100 text-gray-600 px-2 py-0.5 rounded-full">{s}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {sys.users.length > 0 && (
                    <div>
                      <span className="text-xs font-semibold text-gray-400 block mb-1.5">Users</span>
                      <ul className="space-y-0.5">
                        {sys.users.map((u) => (
                          <li key={u} className="text-xs text-gray-600 flex items-start gap-1.5">
                            <span className="w-1 h-1 rounded-full bg-gray-400 mt-1.5 flex-shrink-0" />
                            {u}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                  {sys.exitTrigger && (
                    <div className="bg-indigo-50 rounded-lg p-3 border border-indigo-100">
                      <span className="text-xs font-semibold text-indigo-700 block mb-1">Exit Trigger</span>
                      <p className="text-xs text-indigo-700">{sys.exitTrigger}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>

          {/* Transformation shifts */}
          {criticalShifts.length > 0 && (
            <div>
              <h2 className="text-base font-bold text-gray-900 mb-3">Critical Operating Model Shifts</h2>
              <div className="space-y-2">
                {criticalShifts.map((shift, i) => (
                  <div key={i} className="bg-white rounded-xl border border-gray-200 p-4 flex items-start gap-4">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-red-600 bg-red-50 px-2 py-0.5 rounded">FROM</span>
                        <span className="text-sm text-gray-600">{shift.from}</span>
                      </div>
                    </div>
                    <ArrowRight className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-semibold text-green-600 bg-green-50 px-2 py-0.5 rounded">TO</span>
                        <span className="text-sm text-gray-700 font-medium">{shift.to}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Tab: Workstreams ── */}
      {activeTab === 'Workstreams' && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {workstreams.map((ws) => (
            <div
              key={ws.id}
              className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5"
            >
              <div className="flex items-start gap-3 mb-3">
                <div
                  className="w-3 h-10 rounded-full flex-shrink-0"
                  style={{ backgroundColor: ws.color }}
                />
                <div>
                  <h3 className="font-bold text-gray-900">{ws.label}</h3>
                  <p className="text-xs text-gray-500 mt-0.5">{ws.owner}</p>
                </div>
              </div>
              <p className="text-sm text-gray-600 mb-3 leading-relaxed">{ws.description}</p>
              <div>
                <span className="text-xs font-semibold text-gray-400 block mb-1.5">Active in</span>
                <div className="flex flex-wrap gap-1">
                  {ws.activePhases.map((pid) => {
                    const ph = phases.find((p) => p.meta.id === pid);
                    return ph ? (
                      <Link
                        key={pid}
                        to={`/implementation/phase/${pid}`}
                        className="text-xs px-2 py-0.5 rounded-full text-white font-medium hover:opacity-80"
                        style={{ backgroundColor: ph.meta.color }}
                      >
                        Phase {ph.meta.number}
                      </Link>
                    ) : null;
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* CTA row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-8">
        <Link
          to="/implementation/tracker"
          className="flex items-center gap-3 p-5 bg-white border-2 border-indigo-200 rounded-2xl hover:border-indigo-400 hover:bg-indigo-50 transition-all group"
        >
          <div className="p-2.5 bg-indigo-100 rounded-xl group-hover:bg-indigo-200 transition-colors">
            <BarChart2 className="w-5 h-5 text-indigo-700" />
          </div>
          <div>
            <div className="font-bold text-gray-900">{isAr ? 'متتبع التقدم' : 'Programme Tracker'}</div>
            <div className="text-sm text-gray-500">{isAr ? 'التكاملات، المخاطر، والجدول الزمني' : 'Integrations, risks & timeline'}</div>
          </div>
          <ArrowRight className={`w-5 h-5 text-indigo-400 ml-auto group-hover:translate-x-1 transition-transform ${isAr ? 'rotate-180' : ''}`} />
        </Link>

        <Link
          to="/implementation/phase/phase-5"
          className="flex items-center gap-3 p-5 bg-white border-2 border-rose-200 rounded-2xl hover:border-rose-400 hover:bg-rose-50 transition-all group"
        >
          <div className="p-2.5 bg-rose-100 rounded-xl group-hover:bg-rose-200 transition-colors">
            <AlertTriangle className="w-5 h-5 text-rose-700" />
          </div>
          <div>
            <div className="font-bold text-gray-900">{isAr ? 'التجربة — موعد 30 يونيو' : 'Pilot — Due June 30'}</div>
            <div className="text-sm text-gray-500">{isAr ? 'معرض شانجان الواحد، تشغيل كامل' : 'One Changan showroom, full operation'}</div>
          </div>
          <ArrowRight className={`w-5 h-5 text-rose-400 ml-auto group-hover:translate-x-1 transition-transform ${isAr ? 'rotate-180' : ''}`} />
        </Link>
      </div>
    </div>
  );
}
